// Second KYC step for Account Type -> Depends //
import React, { useEffect, useMemo, useState } from "react";
import countryOptions from "../../utils/AllCountryList";
import { senderAreaList as areaList } from "../../utils/ArealList";
import { useFormik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import { updateProfile, getCurrencies } from "../../utils/Api";
import { inputValidator } from "../../hooks/validator";
import limits from "../../hooks/limitations";
import { ToastProvider, useToast } from "../common/RemitAssureToast";
import Select from "react-select";

const PersonalDetail = ({
  data,
  accountType,
  initialContact,
  forward,
  back,
  skiper,
}) => {
  const [selected_area_code, setSelectedAreaCode] = useState(
    initialContact?.areaCode || "61"
  );
  const showToast=useToast();
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [ubo_area_code, setUboAreaCode] = useState("61");
  const [duplicate_detail, setDuplicateDetail] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedUboCountry, setSelectedUboCountry] = useState(null);
const [middle_name_optional, setMiddleNameOptional] = useState(false);
const [ubo_middle_name_optional, setUboMiddleNameOptional] = useState(false);
  // Format country options for react-select
  const countrySelectOptions = useMemo(() => {
    if (!countryOptions || countryOptions.length === 0) return [];
    return countryOptions.map((country) => ({
      value: country.name,
      label: country.name,
    }));
  }, [countryOptions]);

  // Setting initial form data from fetched data
  const initial = {
    ubo_first_name: "",
    ubo_middle_name: "",
    ubo_last_name: "",
    ubo_email: "",
    ubo_mobile: "",
    ubo_date_of_birth: "",
    ubo_occupation: "",
    ubo_country_of_birth: "",
    First_name: "",
    Middle_name: "",
    Last_name: "",
    email: "",
    mobile: "",
    Date_of_birth: "",
    occupation: "",
    Country_of_birth: "",
  };

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await getCurrencies();
        if (response?.code === "200" && response?.data?.payin_currencies) {
          setCurrencyOptions(response.data.payin_currencies);
        }
      } catch (error) {
        console.error("Error fetching currencies:", error);
        // Fallback to existing areaList if API fails
        setCurrencyOptions([]);
      }
    };

    fetchCurrencies();
  }, []);

  // Yup validation schema for Personal Details
 const personalSchema = useMemo(() => Yup.object().shape({
  ubo_first_name:
    accountType == "business"
      ? Yup.string().max(limits.name).required().trim()
      : Yup.string().notRequired(),
  ubo_middle_name:
    accountType == "business"
      ? ubo_middle_name_optional
        ? Yup.string().max(limits.name).trim().notRequired()
        : Yup.string().max(limits.name).required("Middle Name is required").trim()
      : Yup.string().notRequired(),
  ubo_last_name:
    accountType == "business"
      ? Yup.string().max(limits.name).required().trim()
      : Yup.string().notRequired(),
  ubo_email:
    accountType == "business"
      ? Yup.string()
        .matches(/^[\w-+\.]+@([\w-]+\.)+[\w-]{2,5}$/, "Invalid email format")
        .max(limits.email.max)
        .required()
      : Yup.string().notRequired(),
  ubo_mobile:
    accountType == "business"
      ? Yup.string()
        .test("valid-mobile", "Invalid number", function (value) {
          if (!value) return false;
          const cleaned = value.replace(/^0+/, "");
          return (
            cleaned.length >= limits.user_mobile.min &&
            cleaned.length <= limits.user_mobile.max
          );
        })
      : Yup.string().notRequired(),
  ubo_date_of_birth:
    accountType == "business"
      ? Yup.string()
        .matches(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format (YYYY-MM-DD required)")
        .test("valid-year-range", "Invalid Date", (value) => {
          if (!value) return false;
          const year = parseInt(value.split("-")[0], 10);
          const currentYear = new Date().getFullYear();
          const minYear = currentYear - 100;
          const maxYear = currentYear - 18;
          return year >= minYear && year <= maxYear;
        })
        .required("Date of Birth is required")
      : Yup.date().notRequired(),
  ubo_occupation:
    accountType == "business"
      ? Yup.string().max(limits.occupation).required().trim()
      : Yup.string().notRequired(),
  ubo_country_of_birth:
    accountType == "business"
      ? Yup.string().required("Country of Birth is required")
      : Yup.string().notRequired(),
  First_name: Yup.string().max(limits.name).required().trim(),
  Middle_name: middle_name_optional
    ? Yup.string().max(limits.name).trim().notRequired()
    : Yup.string().max(limits.name).required("Middle Name is required").trim(),
  Last_name: Yup.string().max(limits.name).required().trim(),
  email: Yup.string()
    .matches(/^[\w-+\.]+@([\w-]+\.)+[\w-]{2,5}$/, "Invalid email format")
    .max(limits.email.max)
    .required(),
  mobile: Yup.string()
    .test("valid-mobile", "Invalid number", function (value) {
      if (!value) return false;
      const cleaned = value.replace(/^0+/, "");
      return (
        cleaned.length >= limits.user_mobile.min &&
        cleaned.length <= limits.user_mobile.max
      );
    }),
  Date_of_birth: Yup.string()
    .matches(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format (YYYY-MM-DD required)")
    .test("valid-year-range", "Invalid Date", (value) => {
      if (!value) return false;
      const year = parseInt(value.split("-")[0], 10);
      const currentYear = new Date().getFullYear();
      const minYear = currentYear - 100;
      const maxYear = currentYear - 18;
      return year >= minYear && year <= maxYear;
    })
    .required("Date of Birth is required"),
  occupation: Yup.string().max(limits.occupation).required().trim(),
  Country_of_birth: Yup.string().required("Country of Birth is required"),
}), [accountType, middle_name_optional, ubo_middle_name_optional]);
  // formik setup for Personal Details
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setValues,
    setFieldValue,
    setFieldTouched,
  } = useFormik({
    initialValues: initial,
    validationSchema: personalSchema,
    onSubmit: async (values) => {
      dataUpdateHandler(values);
    },
  });

  useEffect(() => {
    if (initialContact) {
      setSelectedAreaCode(initialContact.areacode || "61");
    }
    let ubo_mobile;
    let extracted_mobile = "";
    let extracted_ubo_area_code = "61";

    if (data.ubo_mobile) {
      const fullMobile = data.ubo_mobile.substring(1);
      const matchedCountry = currencyOptions.find(country =>
        fullMobile.startsWith(country.phone_code)
      );

      if (matchedCountry) {
        extracted_ubo_area_code = matchedCountry.phone_code;
        ubo_mobile = fullMobile.substring(matchedCountry.phone_code.length);
      } else {
        ubo_mobile = data?.ubo_mobile?.substring(3) || null;
        extracted_ubo_area_code = data?.ubo_mobile?.substring(1, 3) || "61";
      }
      setUboAreaCode(extracted_ubo_area_code);
    }

    if (data.mobile) {
      const fullMobile = data.mobile.substring(1);
      const matchedCountry = currencyOptions.find(country =>
        fullMobile.startsWith(country.phone_code)
      );

      if (matchedCountry) {
        setSelectedAreaCode(matchedCountry.phone_code);
        extracted_mobile = fullMobile.substring(matchedCountry.phone_code.length);
      } else {
        extracted_mobile = data?.mobile?.substring(3) || "";
        setSelectedAreaCode(data?.mobile?.substring(1, 3) || "61");
      }
    }

    setDuplicateDetail(data?.is_checked);
    setMiddleNameOptional(data?.middle_name_check === "True" || data?.middle_name_check === true);
setUboMiddleNameOptional(data?.ubo_middle_name_check === "True" || data?.ubo_middle_name_check === true);
    // Set selected country for react-select
    if (data?.Country_of_birth && data.Country_of_birth !== "none") {
      setSelectedCountry({
        value: data.Country_of_birth,
        label: data.Country_of_birth
      });
    } else {
      setSelectedCountry(null);
    }
    
    if (data?.ubo_country_of_birth && data.ubo_country_of_birth !== "none") {
      setSelectedUboCountry({
        value: data.ubo_country_of_birth,
        label: data.ubo_country_of_birth
      });
    } else {
      setSelectedUboCountry(null);
    }

    setValues({
      ubo_first_name: data?.ubo_first_name || "",
      ubo_middle_name: data?.ubo_middle_name || "",
      ubo_last_name: data?.ubo_last_name || "",
      ubo_email: data?.ubo_email || "",
      ubo_mobile: ubo_mobile || "",
      ubo_date_of_birth: data?.ubo_date_of_birth || "",
      ubo_occupation: data?.ubo_occupation || "",
      ubo_country_of_birth: data?.ubo_country_of_birth || "",
      First_name: data?.First_name || "",
      Middle_name: data?.Middle_name || "",
      Last_name: data?.Last_name || "",
      email: data?.email || "",
      mobile: extracted_mobile,
      Date_of_birth: data?.Date_of_birth || "",
      occupation: data?.occupation || "",
      Country_of_birth: data?.Country_of_birth || "",
    });
  }, [data, initialContact, currencyOptions]);

  // function to handle Skip;
  const handleSkip = () => {
    dataUpdateHandler(values);
    skiper();
  };

  // Date validation for DOB
  useEffect(() => {
    var dtToday = new Date();
    var month = dtToday.getMonth() + 1;
    var day;
    if (month === 2 && dtToday.getDate() === 29) {
      day = dtToday.getDate() - 1;
    } else {
      day = dtToday.getDate();
    }
    var year = dtToday.getFullYear() - 18;
    if (month < 10) month = "0" + month.toString();
    if (day < 10) day = "0" + day.toString();
    var maxDate = year + "-" + month + "-" + day;
    var minDate = year - 100 + "-" + month + "-" + day;
    document.getElementById("dob")?.setAttribute("max", maxDate);
    document.getElementById("dob")?.setAttribute("min", minDate);
    document.getElementById("ubo_dob")?.setAttribute("max", maxDate);
    document.getElementById("ubo_dob")?.setAttribute("min", minDate);
  }, []);

  const changeHandler = (e) => {
    inputValidator(e, handleChange);  
    if (duplicate_detail) {
      inputValidator(
        {
          target: {
            name: "ubo_" + e.target.name.toLowerCase(),
            value: e.target.value,
          },
        },
        handleChange
      );
    }
  };

  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption);
    // Set the value in Formik - if null/undefined, set empty string to trigger validation
    setFieldValue("Country_of_birth", selectedOption ? selectedOption.value : "");
    // Mark as touched to show validation
    setFieldTouched("Country_of_birth", true, false);
    
    if (duplicate_detail) {
      setSelectedUboCountry(selectedOption);
      setFieldValue("ubo_country_of_birth", selectedOption ? selectedOption.value : "");
      setFieldTouched("ubo_country_of_birth", true, false);
    }
  };

  const handleUboCountryChange = (selectedOption) => {
    setSelectedUboCountry(selectedOption);
    setFieldValue("ubo_country_of_birth", selectedOption ? selectedOption.value : "");
    setFieldTouched("ubo_country_of_birth", true, false);
  };

  const handleCountryBlur = () => {
    setFieldTouched("Country_of_birth", true, true);
  };

  const handleUboCountryBlur = () => {
    setFieldTouched("ubo_country_of_birth", true, true);
  };

  const dataUpdateHandler = async (values) => {
    let payload = { ...values };
    let mobile = parseInt(values.mobile, 10);
    if (initialContact.mobile !== "+" + selected_area_code + mobile) {
      payload.mobile = "+" + selected_area_code + mobile;
    } else {
      delete payload["mobile"];
    }
    if (payload.ubo_mobile) {
      payload.ubo_mobile =
        "+" + ubo_area_code + parseInt(payload.ubo_mobile, 10);
    }
    if (initialContact.email === values.email) {
      delete payload["email"];
    }
   if (middle_name_optional || !payload.Middle_name || /^\s*$/.test(payload.Middle_name)) {
  delete payload["Middle_name"];
}
if (ubo_middle_name_optional || !payload.ubo_middle_name) {
  delete payload["ubo_middle_name"];
}

    if (accountType !== "business") {
      delete payload["ubo_name"];
    }

    if (initialContact.email === values.email) {
      delete payload["email"];
    }
payload.middle_name_check = middle_name_optional ? "True" : "False";
payload.ubo_middle_name_check = ubo_middle_name_optional ? "True" : "False";
    await Object.keys(payload).forEach((field) => {
      if (!payload[field]) {
        delete payload[field];
      }
    });
    if (duplicate_detail) {
      payload.is_checked = true;
    } else {
      payload.is_checked = false;
    }
    await updateProfile(payload)
      .then((res) => {
        if (res.code === "200") {
          let user = JSON.parse(sessionStorage.getItem("remi-user-dt"));
          let local = {
            ...res?.data,
            is_digital_Id_verified: user?.is_digital_Id_verified,
          };
          sessionStorage.removeItem("remi-user-dt");
          sessionStorage.setItem("remi-user-dt", JSON.stringify(local));
          forward({ ...payload, email: values.email });
        } else if (res.code === "400") {
          showToast('error','Error',res.message);
        }
      })
      .catch((err) => {
        showToast('error','Error',err.message);
      });
  };

  useEffect(() => {
  if (duplicate_detail) {
    setValues({
      ...values,
      ubo_first_name: values.First_name,
      ubo_last_name: values.Last_name,
      ubo_date_of_birth: values.Date_of_birth,
      ubo_middle_name: values.Middle_name,
      ubo_email: values.email,
      ubo_mobile: values.mobile,
      ubo_occupation: values.occupation,
      ubo_country_of_birth: values.Country_of_birth,
    });
    setUboAreaCode(selected_area_code);
    setSelectedUboCountry(selectedCountry);
    setUboMiddleNameOptional(middle_name_optional); 
  }
}, [duplicate_detail]);

const setCheck = (value) => {
  if (value) {
    setDuplicateDetail(true);
    setUboMiddleNameOptional(middle_name_optional); 
  } else {
    setDuplicateDetail(false);
    setUboMiddleNameOptional(false); 
    setValues({
      ...values,
      ubo_first_name: "",
      ubo_last_name: "",
      ubo_date_of_birth: "",
      ubo_middle_name: "",
      ubo_email: "",
      ubo_mobile: "",
      ubo_occupation: "",
      ubo_country_of_birth: "",
    });
    setUboAreaCode("61");
    setSelectedUboCountry(null);
  }
};

  // Custom styles for react-select
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "rgba(252, 253, 255, 0.81)",
      borderColor: state.isFocused ? "#6414E9" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(100, 20, 233, 0.25)" : "none",
      "&:hover": {
        borderColor: "#6414E9",
      },
      minHeight: "38px",
      ...(state.selectProps.className?.includes('is-invalid') && {
        borderColor: "#dc3545",
        "&:hover": {
          borderColor: "#dc3545",
        },
      }),
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? "#6414E9" 
        : state.isFocused 
        ? "#f0e9ff" 
        : "white",
      color: state.isSelected ? "white" : "#212529",
      "&:hover": {
        backgroundColor: "#f0e9ff",
        color: "#212529",
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    input: (provided) => ({
      ...provided,
      color: "#212529",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
  };

  return (
    <div>
      <section className="kyc">
        <form onSubmit={handleSubmit} noValidate className="single-recipient">
          <div className="row">
            {accountType == "business" ? (
              <h5>Authorised Business Represntative</h5>
            ) : (
              <h5>Personal Details</h5>
            )}
            <div className="col-md-4">
              <div className="input_field">
                <p className="get-text">
                  First Name<span style={{ color: "red" }}>*</span>
                </p>
                <input
                  type="text"
                  name="First_name"
                  value={values.First_name}
                  onChange={changeHandler}
                  maxLength={limits.name}
                  className={clsx(
                    "form-control bg-transparent",
                    { "is-invalid": touched.First_name && errors.First_name },
                    { "is-valid": touched.First_name && !errors.First_name }
                  )}
                  onBlur={handleBlur}
                />
              </div>
            </div>
         <div className="col-md-4">
  <div className="input_field">
    <p className="get-text">
      Middle Name
      {!middle_name_optional && <span style={{ color: "red" }}>*</span>}
    </p>
    <input
      type="text"
      name="Middle_name"
      className={clsx(
        "form-control bg-transparent",
        {
          "is-invalid":
            !middle_name_optional &&
            touched.Middle_name &&
            errors.Middle_name,
        },
        {
          "is-valid":
            !middle_name_optional &&
            touched.Middle_name &&
            !errors.Middle_name,
        }
      )}
      maxLength={limits.name}
      onChange={changeHandler}
      value={values.Middle_name}
      disabled={middle_name_optional}
    />
    {!middle_name_optional && touched.Middle_name && errors.Middle_name && (
      <div className="fv-plugins-message-container mt-1">
        <div className="fv-help-block">
          <span role="alert" className="text-danger">
            {errors.Middle_name}
          </span>
        </div>
      </div>
    )}
    {/* Checkbox to make middle name optional */}
    <div className="d-flex align-items-center gap-2 mt-2">
      <input
        type="checkbox"
        id="middle_name_optional_check"
        checked={middle_name_optional}
        onChange={({ target }) => {
          setMiddleNameOptional(target.checked);
          if (target.checked) {
            setFieldValue("Middle_name", "");
            setFieldTouched("Middle_name", false, false);
            if (duplicate_detail) {
              setFieldValue("ubo_middle_name", "");
              setUboMiddleNameOptional(true);
            }
          } else {
            if (duplicate_detail) {
              setUboMiddleNameOptional(false);
            }
          }
        }}
      />
      <label
        htmlFor="middle_name_optional_check"
        className="mb-0"
        style={{ fontSize: "13px", cursor: "pointer" }}
      >
        Middle name is optional
      </label>
    </div>
  </div>
</div>
            <div className="col-md-4">
              <div className="input_field">
                <p className="get-text">
                  Last Name<span style={{ color: "red" }}>*</span>
                </p>
                <input
                  type="text"
                  name="Last_name"
                  value={values.Last_name}
                  maxLength={limits.name}
                  onChange={changeHandler}
                  className={clsx(
                    "form-control bg-transparent",
                    { "is-invalid": touched.Last_name && errors.Last_name },
                    { "is-valid": touched.Last_name && !errors.Last_name }
                  )}
                  onBlur={handleBlur}
                />
              </div>
            </div>
          </div>
          <div className="row each-row">
            <div className="col-md-6">
              <div className="input_field">
                <p className="get-text">
                  Email<span style={{ color: "red" }}>*</span>
                </p>
                <input
                  type="email"
                  name="email"
                  value={values.email}
                  style={{ backgroundColor: "rgba(252, 253, 255, 0.81)" }}
                  maxLength={limits.email.max}
                  onChange={changeHandler}
                  className={clsx(
                    "form-control bg-transparent",
                    { "is-invalid": touched.email && errors.email },
                    {
                      "is-valid": touched.email && !errors.email,
                    }
                  )}
                />
                {errors.email}
              </div>
            </div>
            <div className="col-md-6">
              <div className="input_field">
                <p className="get-text">
                  Mobile<span style={{ color: "red" }}>*</span>
                </p>
                <div className="row kustom_mobile">
                  <div className="col-md-4 px-0">
                    <select
                      className="form-control form-select bg-transparent"
                      value={selected_area_code}
                      onChange={(e) => {
                        setSelectedAreaCode(e.target.value);
                        if (duplicate_detail) {
                          setUboAreaCode(e.target.value);
                        }
                      }}
                    >
                      {(currencyOptions.length > 0
                        ? currencyOptions
                        : areaList
                      )?.map((area, index) => {
                        const code = area?.phone_code || area?.code;
                        const displayName =
                          area?.country_code?.toUpperCase() || area?.name;
                        return (
                          <option key={index} value={code}>
                            +{code}&nbsp;({displayName})
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className={`col-md-8 px-0`}>
                    <input
                      type="text"
                      name="mobile"
                      value={values.mobile}
                      id="mobile"
                      onChange={changeHandler}
                      onBlur={handleBlur}
                      className={clsx(
                        "form-control bg-transparent",
                        { "is-invalid": touched.mobile && errors.mobile },
                        {
                          "is-valid": touched.mobile && !errors.mobile,
                        }
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row each-row">
            <div className="col-md-4">
              <div className="input_field">
                <p className="get-text">
                  Date of Birth<span style={{ color: "red" }}>*</span>
                </p>
                <input
                  type="date"
                  name="Date_of_birth"
                  value={values.Date_of_birth}
                  id="dob"
                  onChange={changeHandler}
                  className={clsx(
                    "form-control bg-transparent",
                    {
                      "is-invalid":
                        touched.Date_of_birth && errors.Date_of_birth,
                    },
                    {
                      "is-valid":
                        touched.Date_of_birth && !errors.Date_of_birth,
                    }
                  )}
                  onBlur={handleBlur}
                />
                {touched.Date_of_birth &&
                  errors.Date_of_birth &&
                  values.Date_of_birth !== "" && (
                    <div className="fv-plugins-message-container mt-1">
                      <div className="fv-help-block">
                        <span role="alert" className="text-danger">
                          {errors.Date_of_birth}
                        </span>
                      </div>
                    </div>
                  )}
              </div>
            </div>
            <div className="col-md-4 country-h">
              <div className="input_field">
                <p className="get-text">
                  Country of Birth<span style={{ color: "red" }}>*</span>
                </p>
                <Select
                  options={countrySelectOptions}
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  onBlur={handleCountryBlur}
                  placeholder="Search country..."
                  isClearable
                  isSearchable
                  styles={selectStyles}
                  className={clsx({
                    "is-invalid": touched.Country_of_birth && errors.Country_of_birth,
                  })}
                  noOptionsMessage={() => "No countries found"}
                />
                {touched.Country_of_birth && errors.Country_of_birth && (
                  <div className="fv-plugins-message-container mt-1">
                    <div className="fv-help-block">
                      <span role="alert" className="text-danger">
                        {errors.Country_of_birth}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <div className="input_field">
                <p className="get-text">
                  Occupation<span style={{ color: "red" }}>*</span>
                </p>
                <input
                  type="text"
                  name="occupation"
                  value={values.occupation}
                  id="occupation"
                  maxLength={limits.occupation}
                  onChange={changeHandler}
                  className={clsx(
                    "form-control bg-transparent",
                    { "is-invalid": touched.occupation && errors.occupation },
                    {
                      "is-valid": touched.occupation && !errors.occupation,
                    }
                  )}
                  onBlur={handleBlur}
                />
              </div>
            </div>
          </div>
          {accountType == "business" && (
            <>
              <div className="row each-row">
                <div className="d-flex gap-2 mb-4">
                  <input
                    type="checkbox"
                    className="form-check h-32 w-32"
                    title="Select if Ultimate Business Owner is same as Authorised Business Representative"
                    checked={duplicate_detail}
                    onChange={({ target }) => {
                      setCheck(target.checked);
                    }}
                  />
                  <span>
                    Select if Ultimate Business Owner is same as Authorised
                    Business Representative
                  </span>
                </div>
              </div>
              <div className="row each-row">
                <h5 className="mb-0 pb-0">Ultimate Business Owner (UBO)</h5>
                <small className="mb-3">(with over 10% shareholding)</small>
                {/* ubo first name */}
                <div className="col-md-4 mb-4">
                  <div className="input_field">
                    <p className="get-text">
                      First Name<span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="text"
                      name="ubo_first_name"
                      value={values.ubo_first_name}
                      onChange={changeHandler}
                      maxLength={limits.name}
                      className={clsx(
                        "form-control bg-transparent",
                        {
                          "is-invalid":
                            touched.ubo_first_name && errors.ubo_first_name,
                        },
                        {
                          "is-valid":
                            touched.ubo_first_name && !errors.ubo_first_name,
                        }
                      )}
                      onBlur={handleBlur}
                      disabled={duplicate_detail}
                    />
                  </div>
                </div>
                {/* ubo middle name */}
  <div className="col-md-4 mb-4">
  <div className="input_field">
    <p className="get-text">
      Middle Name
      {!ubo_middle_name_optional && <span style={{ color: "red" }}>*</span>}
    </p>
    <input
      type="text"
      name="ubo_middle_name"
      className={clsx(
        "form-control bg-transparent",
        {
          "is-invalid":
            !ubo_middle_name_optional &&
            touched.ubo_middle_name &&
            errors.ubo_middle_name,
        },
        {
          "is-valid":
            !ubo_middle_name_optional &&
            touched.ubo_middle_name &&
            !errors.ubo_middle_name,
        }
      )}
      maxLength={limits.name}
      onChange={changeHandler}
      value={values.ubo_middle_name}
      disabled={duplicate_detail || ubo_middle_name_optional}
    />
    {!ubo_middle_name_optional &&
      touched.ubo_middle_name &&
      errors.ubo_middle_name && (
        <div className="fv-plugins-message-container mt-1">
          <div className="fv-help-block">
            <span role="alert" className="text-danger">
              {errors.ubo_middle_name}
            </span>
          </div>
        </div>
    )}
    {/* Checkbox to make UBO middle name optional */}
    <div className="d-flex align-items-center gap-2 mt-2">
      <input
        type="checkbox"
        id="ubo_middle_name_optional_check"
        checked={ubo_middle_name_optional}
        onChange={({ target }) => {
          setUboMiddleNameOptional(target.checked);
          if (target.checked) {
            setFieldValue("ubo_middle_name", "");
            setFieldTouched("ubo_middle_name", false, false);
          }
        }}
        disabled={duplicate_detail}
      />
      <label
        htmlFor="ubo_middle_name_optional_check"
        className="mb-0"
        style={{ fontSize: "13px", cursor: "pointer" }}
      >
        Middle name is optional
      </label>
    </div>
  </div>
</div>
                {/*  ubo last name */}
                <div className="col-md-4 mb-4">
                  <div className="input_field">
                    <p className="get-text">
                      Last Name<span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="text"
                      name="ubo_last_name"
                      value={values.ubo_last_name}
                      maxLength={limits.name}
                      onChange={changeHandler}
                      className={clsx(
                        "form-control bg-transparent",
                        {
                          "is-invalid":
                            touched.ubo_last_name && errors.ubo_last_name,
                        },
                        {
                          "is-valid":
                            touched.ubo_last_name && !errors.ubo_last_name,
                        }
                      )}
                      onBlur={handleBlur}
                      disabled={duplicate_detail}
                    />
                  </div>
                </div>
                {/* ubo email */}
                <div className="col-md-6 mb-4">
                  <div className="input_field">
                    <p className="get-text">
                      Email<span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="email"
                      name="ubo_email"
                      value={values.ubo_email}
                      style={{ backgroundColor: "rgba(252, 253, 255, 0.81)" }}
                      maxLength={limits.email.max}
                      onChange={changeHandler}
                      className={clsx(
                        "form-control bg-transparent",
                        { "is-invalid": touched.ubo_email && errors.ubo_email },
                        {
                          "is-valid": touched.ubo_email && !errors.ubo_email,
                        }
                      )}
                      disabled={duplicate_detail}
                    />
                  </div>
                </div>
                {/* ubo mno  */}
                <div className="col-md-6 mb-4">
                  <div className="input_field">
                    <p className="get-text">
                      Mobile<span style={{ color: "red" }}>*</span>
                    </p>
                    <div className="row kustom_mobile">
                      <div className="col-md-4 px-0">
                        <select
                          className="form-control form-select bg-transparent"
                          value={ubo_area_code}
                          onChange={(e) => setUboAreaCode(e.target.value)}
                          disabled={duplicate_detail}
                        >
                          {(currencyOptions.length > 0
                            ? currencyOptions
                            : areaList
                          )?.map((area, index) => {
                            const code = area?.phone_code || area?.code;
                            const displayName =
                              area?.country_code?.toUpperCase() || area?.name;
                            return (
                              <option key={index} value={code}>
                                +{code}&nbsp;({displayName})
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div className={`col-md-8 px-0`}>
                        <input
                          type="text"
                          name="ubo_mobile"
                          value={values.ubo_mobile}
                          id="ubo_mobile"
                          onChange={changeHandler}
                          onBlur={handleBlur}
                          className={clsx(
                            "form-control bg-transparent",
                            {
                              "is-invalid":
                                touched.ubo_mobile && errors.ubo_mobile,
                            },
                            {
                              "is-valid":
                                touched.ubo_mobile && !errors.ubo_mobile,
                            }
                          )}
                          disabled={duplicate_detail}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {/* ubo  dob */}
                <div className="col-md-4 mb-4">
                  <div className="input_field">
                    <p className="get-text">
                      Date of Birth<span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="date"
                      name="ubo_date_of_birth"
                      value={values.ubo_date_of_birth}
                      id="ubo_dob"
                      onChange={handleChange}
                      className={clsx(
                        "form-control bg-transparent",
                        {
                          "is-invalid":
                            touched.ubo_date_of_birth &&
                            errors.ubo_date_of_birth,
                        },
                        {
                          "is-valid":
                            touched.ubo_date_of_birth &&
                            !errors.ubo_date_of_birth,
                        }
                      )}
                      onBlur={handleBlur}
                      disabled={duplicate_detail}
                    />
                    {touched.ubo_date_of_birth &&
                      errors.ubo_date_of_birth &&
                      values.ubo_date_of_birth !== "" && (
                        <div className="fv-plugins-message-container mt-1">
                          <div className="fv-help-block">
                            <span role="alert" className="text-danger">
                              {errors.ubo_date_of_birth}
                            </span>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
                {/* ubo country of birth */}
                <div className="col-md-4 country-h mb-4">
                  <div className="input_field">
                    <p className="get-text">
                      Country of Birth<span style={{ color: "red" }}>*</span>
                    </p>
                    <Select
                      options={countrySelectOptions}
                      value={selectedUboCountry}
                      onChange={handleUboCountryChange}
                      onBlur={handleUboCountryBlur}
                      placeholder="Select or search country..."
                      isClearable
                      isSearchable
                      styles={selectStyles}
                      isDisabled={duplicate_detail}
                      className={clsx({
                        "is-invalid": touched.ubo_country_of_birth && errors.ubo_country_of_birth,
                      })}
                      noOptionsMessage={() => "No countries found"}
                    />
                    {touched.ubo_country_of_birth && errors.ubo_country_of_birth && (
                      <div className="fv-plugins-message-container mt-1">
                        <div className="fv-help-block">
                          <span role="alert" className="text-danger">
                            {errors.ubo_country_of_birth}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/*  ubo occupation */}
                <div className="col-md-4 mb-4">
                  <div className="input_field">
                    <p className="get-text">
                      Occupation<span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="text"
                      name="ubo_occupation"
                      value={values.ubo_occupation}
                      id="ubo_occupation"
                      maxLength={limits.occupation}
                      onChange={changeHandler}
                      className={clsx(
                        "form-control bg-transparent",
                        {
                          "is-invalid":
                            touched.ubo_occupation && errors.ubo_occupation,
                        },
                        {
                          "is-valid":
                            touched.ubo_occupation && !errors.ubo_occupation,
                        }
                      )}
                      onBlur={handleBlur}
                      disabled={duplicate_detail}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="next-step">
            {accountType == "business" ? (
              <>
                <button
                  type="button"
                  className="SKip back-btn"
                  onClick={() => back()}
                >
                  Back
                </button>
                <button type="submit" className="login_button">
                  Continue{" "}
                  <img
                    src="assets/img/home/Union.png"
                    className="vission_image"
                    alt="alt_image"
                  />
                </button>
              </>
            ) : (
              <button type="submit" className="login_button">
                Next <b>Step</b>{" "}
                <img
                  src="assets/img/home/Union.png"
                  className="vission_image"
                  alt="alt_image"
                />
              </button>
            )}
            <button type="button" onClick={() => handleSkip()} className="SKip">
              Skip
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default PersonalDetail;